/**
 * How each genre looks: a gradient and one oversized glyph — the genre tiles,
 * the genre page's banner, and the chips. Keyed by the platform's permanent
 * genre ids (`GAME_GENRES` in @fias/db-types); a genre this file does not know
 * yet gets the neutral look rather than breaking.
 *
 * Glyphs are plain text characters, not emoji or images, so they render the
 * same in the app and in a static snapshot. Characters that some systems draw
 * as emoji carry the text-presentation selector.
 */

/** Asks for the text (not emoji) form of the character before it. */
const TEXT_FORM = String.fromCodePoint(0xfe0e);

export interface GenreLook {
  /** The bright stop (top right). */
  from: string;
  /** The deep stop (bottom left, under the label): white text reads on it. */
  to: string;
  glyph: string;
}

const LOOKS: Record<string, GenreLook> = {
  action: { from: '#FF6A3D', to: '#E0245E', glyph: '✸' },
  rpg: { from: '#9B6BFF', to: '#4B2FD1', glyph: `⚔${TEXT_FORM}` },
  board: { from: '#34C77B', to: '#0E7A5F', glyph: '♞' },
  cards: { from: '#F0545F', to: '#8E1838', glyph: `♠${TEXT_FORM}` },
  casual: { from: '#FFB341', to: '#E8501F', glyph: '✿' },
  crafting: { from: '#C2864F', to: '#6B3F22', glyph: `⚒${TEXT_FORM}` },
  flying: { from: '#5CCBFF', to: '#2468E8', glyph: `✈${TEXT_FORM}` },
  match: { from: '#FF6FC8', to: '#9B3BF0', glyph: '◆' },
  platform: { from: '#7BDD55', to: '#18864A', glyph: '▲' },
  puzzle: { from: '#6D9BFF', to: '#5236E0', glyph: '◩' },
  strategy: { from: '#56769A', to: '#1E2E44', glyph: '♜' },
  word: { from: '#FFC93C', to: '#C2410C', glyph: 'Aa' },
  racing: { from: '#FF5A5A', to: '#B0123F', glyph: '⚑' },
  sports: { from: '#38D07F', to: '#0F7450', glyph: '◎' },
  simulation: { from: '#45DCCB', to: '#167F95', glyph: '⌂' },
  trivia: { from: '#FF7AC1', to: '#7C3AED', glyph: '?' },
  music: { from: '#FF6A95', to: '#6D32F0', glyph: '♪' },
  educational: { from: '#3DD5C5', to: '#2558E0', glyph: '✎' },
  cozy: { from: '#FFB38A', to: '#C2556B', glyph: '❦' },
  idle: { from: '#F7D154', to: '#A0620A', glyph: '⧗' },
  // A kind of Strategy: its look is Strategy's, warmed.
  'tower-defense': { from: '#7F9BC2', to: '#3A2E52', glyph: '♖' },
};

const NEUTRAL: GenreLook = { from: '#A1A1AA', to: '#52525B', glyph: '✦' };

export function genreLook(genreId: string): GenreLook {
  return LOOKS[genreId] ?? NEUTRAL;
}

/** The genre's gradient, deep at the bottom left where its label sits. */
export function genreGradient(genreId: string): string {
  const { from, to } = genreLook(genreId);
  return `linear-gradient(to top right, ${to}, ${from})`;
}

/** Every genre id this file has a look for (the platform's list, in order). */
export const KNOWN_GENRE_IDS: readonly string[] = Object.keys(LOOKS);
